import { createTheme } from '@mui/material/styles';

const BLUE = '#1e88e5';
const BLUE_DARK = '#1565c0';
const BG = '#f0f2f5';
const PAPER = '#ffffff';
const ELEVATED = '#f8f9fb';
const SIDEBAR = '#0d1721';
const R = 10;

const getLightTheme = (config, locale) =>
    createTheme(
        {
            shape: { borderRadius: R },
            typography: {
                fontFamily: '"Plus Jakarta Sans", "Inter", "Helvetica", "Arial", sans-serif',
                fontSize: 13,
                fontWeightLight: 300,
                fontWeightRegular: 400,
                fontWeightMedium: 500,
                fontWeightBold: 700,
                h1: { fontSize: '2rem',    fontWeight: 700 },
                h2: { fontSize: '1.75rem', fontWeight: 700 },
                h3: { fontSize: '1.5rem',  fontWeight: 600 },
                h4: { fontSize: '1.25rem', fontWeight: 600 },
                h5: { fontSize: '1.1rem',  fontWeight: 600 },
                h6: { fontSize: '1rem',    fontWeight: 600 },
                body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.6 },
                body2: { fontSize: '0.82rem',  fontWeight: 400, lineHeight: 1.5 },
                button: { fontSize: '0.82rem', fontWeight: 600, textTransform: 'none' },
                caption: { fontSize: '0.75rem', fontWeight: 400 },
            },
            components: {
                MuiCssBaseline: {
                    styleOverrides: `
                        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                        * { box-sizing: border-box; }
                        body { background-color: ${BG}; }
                        ::-webkit-scrollbar { width: 5px; height: 5px; }
                        ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 4px; }
                        ::-webkit-scrollbar-track { background: transparent; }
                    `,
                },
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundImage: 'none',
                            backgroundColor: PAPER,
                            border: 'none',
                            borderRadius: R,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.07), 0 0 1px rgba(0,0,0,0.04)',
                        },
                        elevation1: { boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
                        elevation2: { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                        elevation3: { boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
                    },
                },
                MuiCard: {
                    styleOverrides: {
                        root: {
                            backgroundImage: 'none',
                            backgroundColor: PAPER,
                            border: 'none',
                            borderRadius: R,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                        },
                    },
                },
                MuiButton: {
                    styleOverrides: {
                        root: { borderRadius: 8, boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
                        containedPrimary: {
                            background: `linear-gradient(135deg, ${BLUE}, ${BLUE_DARK})`,
                            color: '#ffffff',
                            fontWeight: 700,
                            borderRadius: 8,
                            boxShadow: `0 4px 12px rgba(30,136,229,0.3)`,
                            '&:hover': {
                                background: `linear-gradient(135deg, #42a5f5, ${BLUE})`,
                                boxShadow: `0 6px 16px rgba(30,136,229,0.4)`,
                            },
                        },
                        outlined: {
                            borderRadius: 8,
                            borderColor: 'rgba(30,136,229,0.35)',
                            color: BLUE_DARK,
                            '&:hover': { borderColor: BLUE, backgroundColor: 'rgba(30,136,229,0.05)' },
                        },
                        text: {
                            borderRadius: 8,
                            color: BLUE_DARK,
                            '&:hover': { backgroundColor: 'rgba(30,136,229,0.06)' },
                        },
                    },
                },
                MuiIconButton: {
                    styleOverrides: {
                        root: { borderRadius: 8 },
                    },
                },
                MuiDrawer: {
                    styleOverrides: {
                        paper: {
                            borderRadius: 0,
                            backgroundColor: config?.menuItens || SIDEBAR,
                            backgroundImage: 'none',
                            border: 'none',
                            boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
                        },
                    },
                },
                MuiListItem: {
                    styleOverrides: {
                        root: { backgroundColor: 'transparent' },
                    },
                },
                MuiListItemButton: {
                    styleOverrides: {
                        root: {
                            borderRadius: 8,
                            margin: '2px 8px',
                            width: 'calc(100% - 16px)',
                            color: 'rgba(255,255,255,0.65)',
                            '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.5)' },
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.08)',
                                color: '#ffffff',
                                '& .MuiListItemIcon-root': { color: '#ffffff' },
                            },
                            '&.Mui-selected': {
                                backgroundColor: `rgba(30,136,229,0.25)`,
                                color: '#ffffff',
                                '& .MuiListItemIcon-root': { color: BLUE },
                                '&:hover': { backgroundColor: `rgba(30,136,229,0.3)` },
                            },
                        },
                    },
                },
                MuiListItemText: {
                    styleOverrides: {
                        primary: { color: 'inherit' },
                    },
                },
                MuiMenuItem: {
                    styleOverrides: {
                        root: {
                            borderRadius: 6,
                            '&:hover': { backgroundColor: 'rgba(30,136,229,0.07)' },
                            '&.Mui-selected': { backgroundColor: 'rgba(30,136,229,0.1)', color: BLUE_DARK },
                        },
                    },
                },
                MuiMenu: {
                    styleOverrides: {
                        paper: {
                            borderRadius: R,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            border: '1px solid rgba(0,0,0,0.06)',
                        },
                        list: { padding: '4px' },
                    },
                },
                MuiInputBase: {
                    styleOverrides: {
                        root: { borderRadius: 8 },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            borderRadius: 8,
                            backgroundColor: ELEVATED,
                            '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderRadius: 8 },
                            '&:hover fieldset': { borderColor: 'rgba(30,136,229,0.4)' },
                            '&.Mui-focused fieldset': { borderColor: BLUE },
                        },
                    },
                },
                MuiFilledInput: {
                    styleOverrides: {
                        root: {
                            borderRadius: 8,
                            backgroundColor: ELEVATED,
                            '&:hover': { backgroundColor: '#f0f4f8' },
                            '&.Mui-focused': { backgroundColor: ELEVATED },
                        },
                    },
                },
                MuiTableContainer: {
                    styleOverrides: {
                        root: { borderRadius: R, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
                    },
                },
                MuiTableCell: {
                    styleOverrides: {
                        root: {
                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                        },
                        head: {
                            backgroundColor: ELEVATED,
                            color: 'rgba(0,0,0,0.5)',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        },
                    },
                },
                MuiTableRow: {
                    styleOverrides: {
                        root: {
                            '&:hover td': { backgroundColor: 'rgba(30,136,229,0.03)' },
                        },
                    },
                },
                MuiChip: {
                    styleOverrides: {
                        root: {
                            borderRadius: 6,
                            fontWeight: 500,
                        },
                        colorPrimary: {
                            backgroundColor: 'rgba(30,136,229,0.1)',
                            color: BLUE_DARK,
                            border: '1px solid rgba(30,136,229,0.2)',
                        },
                    },
                },
                MuiTooltip: {
                    styleOverrides: {
                        tooltip: {
                            borderRadius: 6,
                            backgroundColor: '#1a2332',
                            color: '#ffffff',
                            fontSize: '0.75rem',
                        },
                    },
                },
                MuiDivider: {
                    styleOverrides: {
                        root: { borderColor: 'rgba(0,0,0,0.07)' },
                    },
                },
                MuiDialog: {
                    styleOverrides: {
                        paper: {
                            borderRadius: 14,
                            backgroundImage: 'none',
                            boxShadow: '0 24px 48px rgba(0,0,0,0.16)',
                        },
                    },
                },
                MuiPopover: {
                    styleOverrides: {
                        paper: {
                            borderRadius: R,
                            backgroundImage: 'none',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            border: '1px solid rgba(0,0,0,0.06)',
                        },
                    },
                },
                MuiTab: {
                    styleOverrides: {
                        root: {
                            borderRadius: 6,
                            color: 'rgba(0,0,0,0.45)',
                            fontWeight: 500,
                            '&.Mui-selected': { color: BLUE_DARK, fontWeight: 600 },
                        },
                    },
                },
                MuiTabs: {
                    styleOverrides: {
                        indicator: {
                            background: `linear-gradient(90deg, ${BLUE}, #42a5f5)`,
                            height: 3,
                            borderRadius: '3px 3px 0 0',
                        },
                    },
                },
                MuiAccordion: {
                    styleOverrides: {
                        root: {
                            borderRadius: `${R}px !important`,
                            backgroundImage: 'none',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                            '&:before': { display: 'none' },
                        },
                    },
                },
                MuiLinearProgress: {
                    styleOverrides: {
                        root: { borderRadius: 4, backgroundColor: 'rgba(30,136,229,0.1)' },
                    },
                },
                MuiAvatar: {
                    styleOverrides: {
                        root: { borderRadius: 10 },
                    },
                },
                MuiAppBar: {
                    styleOverrides: {
                        root: {
                            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                        },
                    },
                },
            },
            scrollbarStyles: {
                '&::-webkit-scrollbar': { width: '5px', height: '5px' },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.15)',
                    borderRadius: '4px',
                },
            },
            palette: {
                mode: 'light',
                primary:     { main: config?.primaryColor    || BLUE, light: '#42a5f5', dark: BLUE_DARK },
                secondary:   { main: config?.secondaryColor  || BLUE_DARK },
                toolbar:     { main: config?.toolbarColor    || BLUE_DARK },
                menuItens:   { main: config?.menuItens       || SIDEBAR },
                sub:         { main: config?.sub             || ELEVATED },
                toolbarIcon: { main: config?.toolbarIconColor || '#FFFFFF' },
                divide:      { main: config?.divide          || 'rgba(0,0,0,0.08)' },
                background: {
                    default: config?.backgroundDefault || BG,
                    paper:   config?.backgroundPaper   || PAPER,
                },
                text: {
                    primary:   config?.textPrimary   || '#0d1b2a',
                    secondary: config?.textSecondary || '#4a6080',
                },
                divider: 'rgba(0,0,0,0.07)',
                action: {
                    hover:    'rgba(30,136,229,0.06)',
                    selected: 'rgba(30,136,229,0.1)',
                    disabled: 'rgba(0,0,0,0.26)',
                },
                success: { main: '#00c853' },
                warning: { main: '#ff9800' },
                error:   { main: '#f44336' },
                info:    { main: BLUE },
            },
            backgroundImage: 'none',
        },
        locale
    );

export default getLightTheme;
